// Centralized theme for styled-components
// Modify values here to change the entire app's appearance

export const theme = {
  // Color Palette - Easy to modify
  colors: {
    // Primary Brand Colors
    primary: {
      main: '#A7C7E7',
      light: '#C5DAED', 
      dark: '#8BB5E0',
      darker: '#6FA3D9',
    },
    
    // Secondary Colors
    secondary: {
      main: '#FFE4B5',
      light: '#FFF0D4',
      dark: '#F5D57A',
    },
    
    // Neutral Colors
    gray: {
      50: '#FAFBFC',
      100: '#F4F6F8',
      200: '#E4E7EB',
      300: '#CBD2D9',
      400: '#9AA5B1',
      500: '#6B7280',
      600: '#4B5563',
      700: '#374151',
      800: '#1F2937',
      900: '#111827',
    },
    
    // Status Colors
    success: '#10B981',
    warning: '#F59E0B', 
    error: '#EF4444',
    info: '#3B82F6',
    
    // Background Colors
    background: {
      body: '#FAFBFC',           // Main page background
      paper: '#FFFFFF',          // Card/container backgrounds
      light: '#F8FAFC',          // Light sections
      gradient: 'linear-gradient(135deg, #FAFBFC 0%, #F1F5F9 100%)',  // Gradient backgrounds
      pattern: 'radial-gradient(circle at 25% 25%, #A7C7E7 0%, transparent 50%), radial-gradient(circle at 75% 75%, #FFE4B5 0%, transparent 50%)', // Subtle pattern
    },
    
    // Text Colors
    text: {
      primary: '#111827',
      secondary: '#6B7280',
      muted: '#9CA3AF',
      inverse: '#FFFFFF',
    },
    
    // Border Colors
    border: {
      light: '#E5E7EB',
      main: '#D1D5DB',
      dark: '#9CA3AF',
    },
  },
  
  // Typography - Easy to modify fonts and sizes
  typography: {
    fonts: {
      sans: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      serif: 'Playfair Display, Georgia, serif',
      mono: 'JetBrains Mono, Monaco, "Courier New", monospace',
    },
    
    sizes: {
      xs: '0.75rem',     // 12px
      sm: '0.875rem',    // 14px  
      base: '1rem',      // 16px
      lg: '1.125rem',    // 18px
      xl: '1.25rem',     // 20px
      '2xl': '1.5rem',   // 24px
      '3xl': '1.875rem', // 30px
      '4xl': '2.25rem',  // 36px
      '5xl': '3rem',     // 48px
    },
    
    weights: {
      light: 300,
      normal: 400,
      medium: 500,
      semibold: 600,
      bold: 700,
    },
    
    lineHeights: {
      tight: 1.25,
      normal: 1.5,
      relaxed: 1.75,
    },
  },
  
  // Spacing System - Easy to modify spacing scale
  spacing: {
    xs: '0.25rem',   // 4px
    sm: '0.5rem',    // 8px
    md: '1rem',      // 16px
    lg: '1.5rem',    // 24px
    xl: '2rem',      // 32px
    '2xl': '3rem',   // 48px
    '3xl': '4rem',   // 64px
    '4xl': '6rem',   // 96px
  },
  
  // Border Radius - Easy to modify roundness
  borderRadius: {
    none: '0',
    sm: '0.25rem',   // 4px
    md: '0.5rem',    // 8px
    lg: '0.75rem',   // 12px
    xl: '1rem',      // 16px
    '2xl': '1.5rem', // 24px
    full: '9999px',
  },
  
  // Shadows - Easy to modify depth
  shadows: {
    sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
    '2xl': '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
  },
  
  // Transitions - Easy to modify animations
  transitions: {
    fast: '0.15s ease',
    normal: '0.2s ease',
    slow: '0.3s ease',
  },
  
  // Z-Index Scale - Easy to modify layering
  zIndex: {
    dropdown: 1000,
    sticky: 1020,
    fixed: 1030,
    modalBackdrop: 1040,
    modal: 1050,
    popover: 1060,
    tooltip: 1070,
  },
  
  // Breakpoints - Easy to modify responsive design
  breakpoints: {
    sm: '640px',
    md: '768px', 
    lg: '1024px',
    xl: '1280px',
    '2xl': '1536px',
  },
  
  // Component-specific styling that can be easily modified
  components: {
    button: {
      primary: {
        background: 'linear-gradient(135deg, var(--color-primary) 0%, var(--color-primary-dark) 100%)',
        hover: 'linear-gradient(135deg, var(--color-primary-dark) 0%, var(--color-primary) 100%)',
        padding: '0.75rem 1.5rem',
        borderRadius: 'var(--border-radius-lg)',
      },
      outline: {
        background: 'transparent',
        border: '2px solid var(--color-primary)',
        hover: 'var(--color-primary-light)',
        padding: '0.75rem 1.5rem',
        borderRadius: 'var(--border-radius-lg)',
      },
    },
    
    card: {
      background: 'var(--color-white)',
      borderRadius: 'var(--border-radius-xl)',
      shadow: 'var(--shadow-lg)',
      border: '1px solid var(--color-gray-200)',
      padding: 'var(--spacing-lg)',
    },
    
    input: {
      borderRadius: 'var(--border-radius-md)',
      border: '2px solid var(--color-gray-200)',
      focus: 'var(--color-primary)',
      padding: '0.75rem 1rem',
    },
  },
};

// CSS Custom Properties for easy theme modification
export const cssVariables = `
  :root {
    /* Colors */
    --color-primary: ${theme.colors.primary.main};
    --color-primary-light: ${theme.colors.primary.light};
    --color-primary-dark: ${theme.colors.primary.dark};
    --color-primary-darker: ${theme.colors.primary.darker};
    
    --color-secondary: ${theme.colors.secondary.main};
    --color-secondary-light: ${theme.colors.secondary.light};
    --color-secondary-dark: ${theme.colors.secondary.dark};
    
    --color-white: #FFFFFF;
    --color-black: #000000;
    
    --color-gray-50: ${theme.colors.gray[50]};
    --color-gray-100: ${theme.colors.gray[100]};
    --color-gray-200: ${theme.colors.gray[200]};
    --color-gray-300: ${theme.colors.gray[300]};
    --color-gray-400: ${theme.colors.gray[400]};
    --color-gray-500: ${theme.colors.gray[500]};
    --color-gray-600: ${theme.colors.gray[600]};
    --color-gray-700: ${theme.colors.gray[700]};
    --color-gray-800: ${theme.colors.gray[800]};
    --color-gray-900: ${theme.colors.gray[900]};
    
    --color-success: ${theme.colors.success};
    --color-warning: ${theme.colors.warning};
    --color-error: ${theme.colors.error};
    --color-info: ${theme.colors.info};
    
    --color-background-body: ${theme.colors.background.body};
    --color-background-paper: ${theme.colors.background.paper};
    --color-background-light: ${theme.colors.background.light};
    --color-background-gradient: ${theme.colors.background.gradient};
    --color-background-pattern: ${theme.colors.background.pattern};
    
    --color-text-primary: ${theme.colors.text.primary};
    --color-text-secondary: ${theme.colors.text.secondary};
    --color-text-muted: ${theme.colors.text.muted};
    --color-text-inverse: ${theme.colors.text.inverse};
    
    --color-border-light: ${theme.colors.border.light};
    --color-border-main: ${theme.colors.border.main};
    --color-border-dark: ${theme.colors.border.dark};
    
    /* Typography */
    --font-sans: ${theme.typography.fonts.sans};
    --font-serif: ${theme.typography.fonts.serif};
    --font-mono: ${theme.typography.fonts.mono};
    
    --text-xs: ${theme.typography.sizes.xs};
    --text-sm: ${theme.typography.sizes.sm};
    --text-base: ${theme.typography.sizes.base};
    --text-lg: ${theme.typography.sizes.lg};
    --text-xl: ${theme.typography.sizes.xl};
    --text-2xl: ${theme.typography.sizes['2xl']};
    --text-3xl: ${theme.typography.sizes['3xl']};
    --text-4xl: ${theme.typography.sizes['4xl']};
    --text-5xl: ${theme.typography.sizes['5xl']};
    
    --font-light: ${theme.typography.weights.light};
    --font-normal: ${theme.typography.weights.normal};
    --font-medium: ${theme.typography.weights.medium};
    --font-semibold: ${theme.typography.weights.semibold};
    --font-bold: ${theme.typography.weights.bold};
    
    --leading-tight: ${theme.typography.lineHeights.tight};
    --leading-normal: ${theme.typography.lineHeights.normal};
    --leading-relaxed: ${theme.typography.lineHeights.relaxed};
    
    /* Spacing */
    --spacing-xs: ${theme.spacing.xs};
    --spacing-sm: ${theme.spacing.sm};
    --spacing-md: ${theme.spacing.md};
    --spacing-lg: ${theme.spacing.lg};
    --spacing-xl: ${theme.spacing.xl};
    --spacing-2xl: ${theme.spacing['2xl']};
    --spacing-3xl: ${theme.spacing['3xl']};
    --spacing-4xl: ${theme.spacing['4xl']};
    
    /* Border Radius */
    --border-radius-none: ${theme.borderRadius.none};
    --border-radius-sm: ${theme.borderRadius.sm};
    --border-radius-md: ${theme.borderRadius.md};
    --border-radius-lg: ${theme.borderRadius.lg};
    --border-radius-xl: ${theme.borderRadius.xl};
    --border-radius-2xl: ${theme.borderRadius['2xl']};
    --border-radius-full: ${theme.borderRadius.full};
    
    /* Shadows */
    --shadow-sm: ${theme.shadows.sm};
    --shadow-md: ${theme.shadows.md};
    --shadow-lg: ${theme.shadows.lg};
    --shadow-xl: ${theme.shadows.xl};
    --shadow-2xl: ${theme.shadows['2xl']};
    
    /* Transitions */
    --transition-fast: ${theme.transitions.fast};
    --transition-normal: ${theme.transitions.normal};
    --transition-slow: ${theme.transitions.slow};
    
    /* Z-Index */
    --z-dropdown: ${theme.zIndex.dropdown};
    --z-sticky: ${theme.zIndex.sticky};
    --z-fixed: ${theme.zIndex.fixed};
    --z-modal-backdrop: ${theme.zIndex.modalBackdrop};
    --z-modal: ${theme.zIndex.modal};
    --z-popover: ${theme.zIndex.popover};
    --z-tooltip: ${theme.zIndex.tooltip};
  }
`;

export default theme;